// RevisionDrawer — slide-in panel showing the revision history for an
// estimate or sales request. Clicking a revision loads its full snapshot
// for side-by-side comparison.
//
// Props:
//   open        boolean — controls visibility
//   onClose     () => void
//   title       string — e.g. "Estimate #12 History"
//   listFn      () => Promise<revision[]>  — api.listEstimateRevisions(id)
//   snapshotFn  (revId) => Promise<snapshot>
//   renderSnapshot (snapshot) => ReactNode — caller provides the summary view

import { useEffect, useState } from 'react';
import { fmtDate } from './UI';

const drawerOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
  display: 'flex', justifyContent: 'flex-end',
};
const drawerPanel = {
  width: 520, maxWidth: '95vw', height: '100%',
  background: '#fff', overflowY: 'auto',
  boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
  display: 'flex', flexDirection: 'column',
};
const drawerHeader = {
  background: '#1F2937', color: '#fff',
  padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flexShrink: 0,
};
const revItem = (selected) => ({
  padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6',
  background: selected ? '#FFF7ED' : '#fff',
  borderLeft: selected ? '3px solid #E36F2C' : '3px solid transparent',
});

export default function RevisionDrawer({ open, onClose, title, listFn, snapshotFn, renderSnapshot }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setRevisions([]); setSelectedId(null); setSnapshot(null); setError(''); return; }
    setLoading(true);
    listFn()
      .then(r => setRevisions(r))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const selectRevision = (rev) => {
    if (selectedId === rev.id) { setSelectedId(null); setSnapshot(null); return; }
    setSelectedId(rev.id);
    setSnapLoading(true);
    snapshotFn(rev.id)
      .then(s => setSnapshot(s))
      .catch(e => setError(e.message))
      .finally(() => setSnapLoading(false));
  };

  if (!open) return null;

  return (
    <div style={drawerOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={drawerPanel}>
        <div style={drawerHeader}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{title || 'Revision History'}</span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {error && (
            <div style={{ margin: 12, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 6, fontSize: 13 }}>
              {error}
            </div>
          )}
          {loading && <p style={{ padding: 16, color: '#6B7280', fontSize: 13 }}>Loading history…</p>}
          {!loading && revisions.length === 0 && !error && (
            <p style={{ padding: 16, color: '#6B7280', fontSize: 13 }}>No revisions recorded yet.</p>
          )}
          {revisions.map(rev => (
            <div key={rev.id}>
              <div style={revItem(selectedId === rev.id)} onClick={() => selectRevision(rev)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
                    {rev.edited_by_name || 'Unknown'}
                  </span>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {fmtDate(rev.edited_at)}
                  </span>
                </div>
                {rev.change_note && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{rev.change_note}</div>
                )}
                <div style={{ fontSize: 11, color: '#E36F2C', marginTop: 3 }}>
                  {selectedId === rev.id ? '▲ Hide snapshot' : '▼ View snapshot'}
                </div>
              </div>
              {selectedId === rev.id && (
                <div style={{ padding: '12px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  {snapLoading ? (
                    <p style={{ fontSize: 13, color: '#6B7280' }}>Loading snapshot…</p>
                  ) : snapshot && renderSnapshot ? (
                    renderSnapshot(snapshot)
                  ) : snapshot ? (
                    <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#374151', margin: 0 }}>
                      {JSON.stringify(snapshot, null, 2)}
                    </pre>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
